import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';
import { installPreviewInterceptor, clearPreviewSession } from '../lib/previewSession';

const AuthContext = createContext(null);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Install the preview-mode header interceptor once at module load
installPreviewInterceptor();

// Setup axios interceptor for token refresh
let isRefreshing = false;
let refreshPromise = null;

axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    // iter303 — Offboarding: the server hard-blocks archived/deactivated accounts with a
    // 401 ACCOUNT_DISABLED on EVERY request (and clears cookies on refresh). Force an
    // immediate logout + redirect so a just-archived user cannot keep using a loaded tab.
    const detail = error.response?.data?.detail;
    if (error.response?.status === 401 && detail === 'ACCOUNT_DISABLED') {
      try { await axios.post(`${API}/auth/logout`, {}, { withCredentials: true }); } catch { /* ignore */ }
      if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
        window.location.assign('/login?disabled=1');
      }
      return Promise.reject(error);
    }
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !originalRequest.url.includes('/auth/login') &&
      !originalRequest.url.includes('/auth/refresh')
    ) {
      originalRequest._retry = true;
      try {
        if (!isRefreshing) {
          isRefreshing = true;
          refreshPromise = axios.post(`${API}/auth/refresh`, {}, { withCredentials: true });
        }
        await refreshPromise;
        isRefreshing = false;
        refreshPromise = null;
        return axios(originalRequest);
      } catch (refreshError) {
        isRefreshing = false;
        refreshPromise = null;
        return Promise.reject(refreshError);
      }
    }
    return Promise.reject(error);
  }
);

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : (typeof e === "string" ? e : JSON.stringify(e)))).filter(Boolean).join(" ");
  if (typeof detail === "object") {
    // FastAPI sometimes wraps validation errors as a single object {msg, loc, type, ...}
    if (typeof detail.msg === "string") return detail.msg;
    if (typeof detail.error === "string") return detail.error;
    if (typeof detail.message === "string") return detail.message;
    // Fallback: pretty-print as JSON instead of [object Object]
    try { return JSON.stringify(detail); } catch { return "Unexpected error from server"; }
  }
  return String(detail);
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const { data } = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(data);
    } catch (error) {
      setUser(false);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      await axios.post(
        `${API}/auth/login`,
        { email, password },
        { withCredentials: true }
      );
      // Re-fetch via /auth/me so we get the permissions array
      const { data } = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(data);
      return { success: true };
    } catch (error) {
      return { success: false, error: formatApiErrorDetail(error.response?.data?.detail) || error.message };
    }
  };

  const register = async (name, email, password, role) => {
    try {
      const { data } = await axios.post(
        `${API}/auth/register`,
        { name, email, password, role },
        { withCredentials: true }
      );
      setUser(data);
      return { success: true };
    } catch (error) {
      return { success: false, error: formatApiErrorDetail(error.response?.data?.detail) || error.message };
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      // Clear any stale portal-preview session so the next login isn't blocked
      // by the read-only middleware (it would attach X-Preview-As-User-Id).
      clearPreviewSession();
      setUser(false);
    } catch (error) {
      // Even on logout failure, clear the preview header so login flows aren't blocked.
      clearPreviewSession();
      console.error('Logout error:', error);
    }
  };

  const hasPermission = (perm) => {
    if (!user || typeof user !== 'object') return false;
    if (user.role === 'super_admin' || user.role === 'admin') return true;
    return Array.isArray(user.permissions) && user.permissions.includes(perm);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, login, register, logout, checkAuth, hasPermission }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};